import React from 'react';
import { TextField } from '@mui/material';

/**
 * A TextField for decimal number input with Finnish locale support.
 *
 * Features:
 * - Displays values with comma as decimal separator (Finnish locale)
 * - Accepts both comma and dot as decimal separator input
 * - Stores value internally with dot for API compatibility
 * - Limits decimal places (configurable, default 2)
 * - Shows numeric keyboard on mobile devices
 *
 * @param {string|number} value - The decimal value (stored with dot separator)
 * @param {function} onChange - Called with the new value (dot separator) when changed
 * @param {number} maxDecimals - Maximum decimal places allowed (default: 2). 0 makes it a
 *   whole-number field: no decimal separator is accepted and phones get the digit keypad.
 * @param {number} max - Optional upper bound; values above it are not accepted. Use for
 *   fields the backend also bounds (e.g. a percentage capped at 100) so the user is
 *   stopped at the keystroke instead of by a validation error on save.
 * @param {object} props - Additional props passed to TextField
 */
function DecimalField({
  value,
  onChange,
  maxDecimals = 2,
  max,
  allowNegative = false,
  inputProps,
  ...props
}) {
  // Convert value to display format (with comma)
  const displayValue = value === null || value === undefined || value === ''
    ? ''
    : String(value).replace('.', ',');

  const handleChange = (e) => {
    let newValue = e.target.value;

    // Allow comma as decimal separator (Finnish locale)
    newValue = newValue.replace(',', '.');

    // Only allow valid number characters (whole numbers take no separator at all)
    const sign = allowNegative ? '-?' : '';
    const fraction = maxDecimals > 0 ? '\\.?[0-9]*' : '';
    const pattern = new RegExp(`^${sign}[0-9]*${fraction}$`);
    if (newValue !== '' && !pattern.test(newValue)) {
      return;
    }

    // Limit decimal places
    const parts = newValue.split('.');
    if (parts.length > 1 && parts[1].length > maxDecimals) {
      newValue = parts[0] + '.' + parts[1].slice(0, maxDecimals);
    }

    // Reject values above the cap rather than clamping: clamping silently rewrites what
    // the user typed, which reads as the field eating keystrokes.
    if (max !== undefined && newValue !== '' && parseFloat(newValue) > max) {
      return;
    }

    onChange(newValue);
  };

  return (
    <TextField
      type="text"
      value={displayValue}
      onChange={handleChange}
      inputProps={{
        inputMode: maxDecimals > 0 ? 'decimal' : 'numeric',
        ...inputProps,
      }}
      {...props}
    />
  );
}

export default DecimalField;
